import Axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

export type ServiceModuleValue = string;

export interface ApiClientConfig {
  baseUrl: string;
  defaultOrg?: string;
  ignoreStatuses?: number[];
  services: Record<string, string>;
  serviceDomains?: Record<string, string>;
  resolve: (params: { url: string; service: string }) => string;
  requireAuth?: boolean;
  authToken?: string | null;
  /** Services whose 401 responses trigger refresh + single retry. */
  tokenRefreshServices?: string[];
  /** Refresh callback for `tokenRefreshServices` 401s. Resolves to new bearer or null. Caller must dedupe concurrent calls. */
  onTokenRefresh?: () => Promise<string | null>;
  /** Services whose CORS does not allow the `X-Organization-Id` header — skip sending it for these. */
  skipOrganizationHeaderServices?: string[];
  /**
   * Services whose CORS allows ONLY `Authorization` + `Content-Type` request headers.
   * For these, `X-Requested-With` and `X-Organization-Id` are omitted and `withCredentials`
   * is forced off, so the strict preflight (`Access-Control-Allow-Headers`) passes.
   */
  corsRestrictedServices?: string[];
}

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export function report(error: any) {
  console.error("API Error:", error);
}

export default function getAxiosInstance(config: {
  service: ServiceModuleValue;
  organization?: string;
  apiConfig?: ApiClientConfig;
  [key: string]: any;
}): AxiosInstance {
  const { service, organization, apiConfig } = config;

  if (!apiConfig) {
    throw new Error("API configuration is required");
  }

  const {
    baseUrl,
    defaultOrg,
    ignoreStatuses = [401, 423],
    requireAuth = true,
    authToken,
    serviceDomains,
    tokenRefreshServices = [],
    onTokenRefresh,
    skipOrganizationHeaderServices = [],
    corsRestrictedServices = [],
  } = apiConfig;

  const shouldRefreshOnUnauthorized =
    tokenRefreshServices.includes(service) && !!onTokenRefresh;

  // Services whose CORS allows only Authorization + Content-Type request headers.
  const isCorsRestricted = corsRestrictedServices.includes(service);

  const resolvedBaseUrl = serviceDomains?.[service] || baseUrl;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  // X-Requested-With is not CORS-safelisted; strict services reject it in preflight.
  if (!isCorsRestricted) {
    headers["X-Requested-With"] = "XMLHttpRequest";
  }

  // Some services (e.g. payment-service) don't allow X-Organization-Id in CORS — skip it.
  // Strict-CORS services also exclude it.
  if (!isCorsRestricted && !skipOrganizationHeaderServices.includes(service)) {
    headers["X-Organization-Id"] = organization || defaultOrg || "";
  }

  // Add Authorization header if token is available
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  // Strict-CORS services disallow credentials (no cookies) regardless of requireAuth.
  const useCredentials = requireAuth && !isCorsRestricted;

  const axios = Axios.create({
    baseURL: resolvedBaseUrl,
    headers,
    withCredentials: useCredentials,
  });

  if (useCredentials) {
    axios.defaults.withCredentials = true;
    axios.defaults.withXSRFToken = true;
  }

  axios.interceptors.request.use(
    function (requestConfig) {
      requestConfig.url = apiConfig.resolve({
        ...config,
        url: requestConfig.url!,
        service,
      });
      return requestConfig;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const requestConfig = error?.config as RetryableRequestConfig | undefined;

      // 401 → refresh + replay once. Dedup is the caller's job (onTokenRefresh).
      if (
        status === 401 &&
        shouldRefreshOnUnauthorized &&
        requestConfig &&
        !requestConfig._retry
      ) {
        requestConfig._retry = true;
        const newToken = await onTokenRefresh!();
        if (newToken) {
          requestConfig.headers.set("Authorization", `Bearer ${newToken}`);
          return axios.request(requestConfig);
        }
      }

      if (error?.ignore || ignoreStatuses.includes(status)) {
        return Promise.reject(error);
      }

      if (!error.response) {
        report(error);
        return Promise.reject(error);
      }

      report(error);
      return Promise.reject(error);
    },
  );

  return axios;
}
